import { useEffect, useState } from 'react';
import type { MailSearchType } from '@/types/components';
import type { MailFolder } from '@/types/mail';

const MIN_SEARCH_VALUE_LENGTH = 3;

export const useMailSearch = (folder: MailFolder | null) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [submittedQuery, setSubmittedQuery] = useState('');
  const [selectedType, setSelectedType] = useState<MailSearchType | null>(null);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [hasMinLengthError, setHasMinLengthError] = useState(false);
  const [returnCount, setReturnCount] = useState(0);

  const searchTypes = folder?.searchTypes ?? [];
  const isSelectedTypeAvailable = searchTypes.some(
    ({ type }) => type === selectedType,
  );
  const searchType =
    (isSelectedTypeAvailable ? selectedType : searchTypes[0]?.type) ?? null;
  const isSearching = submittedQuery.length > 0;

  const changeQuery = (value: string) => {
    setSearchQuery(value);

    if (!value) {
      setSubmittedQuery('');
    }
  };

  const submitSearch = () => {
    const value = searchQuery.trim();

    if (value.length < MIN_SEARCH_VALUE_LENGTH) {
      setHasMinLengthError(true);
      return;
    }

    setSubmittedQuery(value);
  };

  const dismissMinLengthError = () => setHasMinLengthError(false);

  const resetSearch = () => {
    setSearchQuery('');
    setSubmittedQuery('');
    setIsSearchOpen(false);
  };

  const openSearch = () => setIsSearchOpen(true);

  const hideSearch = () => {
    setIsSearchOpen(false);
    setReturnCount((count) => count + 1);
  };

  const closeSearch = () => {
    resetSearch();
    setReturnCount((count) => count + 1);
  };

  useEffect(() => {
    setSearchQuery('');
    setSubmittedQuery('');
  }, [folder?.type]);

  return {
    searchTypes,
    searchType,
    searchQuery,
    submittedQuery,
    isSearching,
    isSearchOpen,
    hasMinLengthError,
    returnCount,
    changeQuery,
    changeSearchType: setSelectedType,
    submitSearch,
    dismissMinLengthError,
    openSearch,
    hideSearch,
    closeSearch,
    resetSearch,
  };
};
